/**
 * See360 Hotspot Manager
 * Renders clickable hotspot dots with per-frame position tracking.
 * If a hotspot has `positions` keyframes, it interpolates smoothly between them.
 * Falls back to fixed-position with ±3 frame tolerance for legacy hotspots.
 */

export interface HotspotPosition {
  frame: number;
  x: number;
  y: number;
}

export interface HotspotData {
  id: string;
  frameIndex: number;
  xPercent: number;
  yPercent: number;
  positions?: HotspotPosition[] | null;
  label: string;
  description?: string;
  imageUrl?: string;
  category?: string;
  viewType?: string;  // 'EXTERIOR' | 'INTERIOR'
}

export interface HotspotManagerOptions {
  container: HTMLElement;
  hotspots: HotspotData[];
  frameCount: number;
  frameTolerance?: number;  // for legacy hotspots without positions (default: 3)
  onHotspotClick?: (hotspot: HotspotData) => void;
}

export class HotspotManager {
  private container: HTMLElement;
  private hotspots: HotspotData[];
  private wrappers: Map<string, HTMLElement> = new Map();
  private onHotspotClick?: (hotspot: HotspotData) => void;
  private frameCount: number;
  private frameTolerance: number;

  constructor(options: HotspotManagerOptions) {
    this.container = options.container;
    this.hotspots = options.hotspots;
    this.frameCount = options.frameCount;
    this.frameTolerance = options.frameTolerance ?? 3;
    this.onHotspotClick = options.onHotspotClick;
    this.injectStyles();
    this.createHotspotElements();
  }

  private injectStyles(): void {
    if (document.getElementById('see360-hotspot-styles')) return;
    const style = document.createElement('style');
    style.id = 'see360-hotspot-styles';
    style.textContent = `
      @keyframes see360-hotspot-pulse {
        0%, 100% { box-shadow: 0 0 12px rgba(0, 212, 255, 0.4); }
        50% { box-shadow: 0 0 20px rgba(0, 212, 255, 0.7); }
      }
    `;
    document.head.appendChild(style);
  }

  private createHotspotElements(): void {
    for (const hotspot of this.hotspots) {
      const wrapper = document.createElement('div');
      wrapper.style.cssText = `
        position: absolute; left: ${hotspot.xPercent}%; top: ${hotspot.yPercent}%;
        transform: translate(-50%, -50%); z-index: 30; display: none;
      `;

      const dot = document.createElement('div');
      dot.style.cssText = `
        width: 28px; height: 28px; border-radius: 50%;
        background: rgba(0, 212, 255, 0.8); border: 2px solid white;
        cursor: pointer; transition: transform 0.2s, box-shadow 0.2s;
        box-shadow: 0 0 12px rgba(0, 212, 255, 0.4);
        animation: see360-hotspot-pulse 2s infinite;
      `;

      const tip = document.createElement('div');
      let html = `<strong>${hotspot.label}</strong>`;
      if (hotspot.category) html = `<span style="color:#00d4ff;font-size:11px;text-transform:uppercase;letter-spacing:1px">${hotspot.category}</span><br>${html}`;
      if (hotspot.description) html += `<br><span style="opacity:0.8;font-size:13px">${hotspot.description}</span>`;
      if (hotspot.imageUrl) html += `<br><img src="${hotspot.imageUrl}" style="width:100%;border-radius:4px;margin-top:8px" />`;
      tip.innerHTML = html;
      tip.style.cssText = `
        position: absolute; bottom: 36px; left: 50%; transform: translateX(-50%);
        background: rgba(0,0,0,0.85); color: white;
        padding: 10px 14px; border-radius: 8px; font-size: 14px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        max-width: 260px; min-width: 120px; white-space: normal;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3); backdrop-filter: blur(8px);
        pointer-events: none; display: none; z-index: 100;
      `;

      dot.addEventListener('mouseenter', () => {
        dot.style.transform = 'scale(1.3)';
        dot.style.boxShadow = '0 0 20px rgba(0, 212, 255, 0.8)';
        tip.style.display = 'block';
      });
      dot.addEventListener('mouseleave', () => {
        dot.style.transform = 'scale(1)';
        dot.style.boxShadow = '0 0 12px rgba(0, 212, 255, 0.4)';
        tip.style.display = 'none';
      });
      dot.addEventListener('click', () => {
        this.onHotspotClick?.(hotspot);
      });

      wrapper.appendChild(tip);
      wrapper.appendChild(dot);
      this.container.appendChild(wrapper);
      this.wrappers.set(hotspot.id, wrapper);
    }
  }

  showForFrame(frameIndex: number): void {
    for (const hotspot of this.hotspots) {
      const wrapper = this.wrappers.get(hotspot.id);
      if (!wrapper) continue;

      // Hide-all signal (interior mode)
      if (frameIndex < 0) {
        wrapper.style.display = 'none';
        continue;
      }

      // Match dashboard behavior: show only on the exact placed frame
      if (hotspot.frameIndex !== frameIndex) {
        wrapper.style.display = 'none';
        continue;
      }

      // Hotspot is on this frame — find its position
      // Use per-frame position if available (tracks the feature precisely)
      if (hotspot.positions && hotspot.positions.length > 0) {
        const pos = hotspot.positions.find(p => p.frame === frameIndex);
        if (pos) {
          wrapper.style.left = `${pos.x}%`;
          wrapper.style.top = `${pos.y}%`;
        }
        // else: keep the initial xPercent/yPercent position set in createHotspotElements
      }

      wrapper.style.display = 'block';
    }
  }

  /**
   * Interpolate x,y position between the two nearest keyframes.
   * Returns null if the frame is outside the keyframe range or in a gap.
   * Handles wrap-around correctly (e.g., positions at frames 0-9 and 27-35).
   */
  private interpolatePosition(positions: HotspotPosition[], frame: number): { x: number; y: number } | null {
    if (positions.length === 0) return null;
    if (positions.length === 1) {
      const p = positions[0];
      return this.wrappedFrameDist(p.frame, frame) <= this.frameTolerance
        ? { x: p.x, y: p.y }
        : null;
    }

    // Sort by frame ascending
    const sorted = [...positions].sort((a, b) => a.frame - b.frame);

    // Exact match?
    const exact = sorted.find(p => p.frame === frame);
    if (exact) return { x: exact.x, y: exact.y };

    // Find the two closest keyframes (checking gaps)
    // Check between consecutive sorted positions
    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i];
      const b = sorted[i + 1];
      if (frame > a.frame && frame < b.frame) {
        // Check if there's a gap (more frames between keyframes than tolerance allows)
        const gap = b.frame - a.frame;
        if (gap > 2 * this.frameTolerance + 1) {
          // This is a visibility gap — hotspot should be hidden here
          return null;
        }
        // Lerp between the two keyframes
        const t = (frame - a.frame) / (b.frame - a.frame);
        return {
          x: a.x + (b.x - a.x) * t,
          y: a.y + (b.y - a.y) * t,
        };
      }
    }

    // Frame is before the first or after the last keyframe
    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    // Check wrap-around: is the frame in the wrap zone (after last, before first)?
    // e.g., positions at 27-35 and 0-9 with frameCount=36: frame 34 → close to last(35)
    const distToFirst = this.wrappedFrameDist(frame, first.frame);
    const distToLast = this.wrappedFrameDist(frame, last.frame);

    if (distToFirst <= this.frameTolerance) {
      return { x: first.x, y: first.y };
    }
    if (distToLast <= this.frameTolerance) {
      return { x: last.x, y: last.y };
    }

    // Not close enough to any keyframe
    return null;
  }

  /** Calculate shortest frame distance with wrapping */
  private wrappedFrameDist(a: number, b: number): number {
    const diff = Math.abs(a - b);
    return Math.min(diff, this.frameCount - diff);
  }

  /** Legacy: check if fixed-position hotspot is within tolerance */
  private isWithinRange(hotspotFrame: number, currentFrame: number): boolean {
    if (currentFrame < 0) return false;
    return this.wrappedFrameDist(hotspotFrame, currentFrame) <= this.frameTolerance;
  }

  destroy(): void {
    for (const wrapper of this.wrappers.values()) {
      wrapper.remove();
    }
    this.wrappers.clear();
  }
}
